<!--
  Forbrukerrådet - Kommunetesten
  @link https://gitlab01.copyleft.no/FBR/kommunetesten-new
  @author Anders Evenrud <anders.evenrud@copyleft.no>
-->
<template>
  <div id="kommunetesten-new" :data-embedded="embedded">
    <header>
      <form method="get" action="" role="search">
        <Search placeholder="Søk etter kommune eller fylke" @select="onSearchSelect" :year="year" />
      </form>

      <nav class="breadcrumb-list">
        <Breadcrumbs :county="county" :municipality="municipality" :year="year" :borders="!embedded">
          <div v-if="!embedded">
            <ReadMore />
          </div>
        </Breadcrumbs>
      </nav>
    </header>

    <template v-if="loading">
      <Loading />
    </template>

    <template v-else>
      <router-view :county="county" :municipality="municipality" :year="year" :embedded="embedded" />

      <template v-if="embedded">
        <Button href="https://www.forbrukerradet.no/kommunetesten" target="_blank">
          <font-awesome-icon icon="sync" />
          <span>Se hele kommunetesten {{ year || '(todo)' }}</span>
        </Button>

        <footer>
          En tjeneste levert av <a href="https://www.forbrukerradet.no/" target="_blank">Forbrukerrådet</a>
        </footer>
      </template>

      <template v-else>
        <nav class="counties" :key="year">
          <Heading :year="year">Fylkesoversikt</Heading>
          <CountyList :items="filteredCounties(year)" :year="year" />
        </nav>
      </template>
    </template>

    <Modal v-if="modal"
       :county="county"
       :year="year"
       @close="onModalClose"
    >
      <div>
        <PositionsText :data="modal" />

        <Button :href="iframeExternalLink(modal)" target="_blank">
          <font-awesome-icon icon="external-link-alt" />
          Se mer på forbrukerradet.no
        </Button>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss">
@import "./assets/variables";

#kommunetesten-new {
  font-size: $font-size;
  color: $font-color;
  line-height: 1;
  width: 100%;
  max-width: 768px;
  padding: 0 1em 0 1em;
  margin: 0 auto;
  box-sizing: border-box;

  a {
    color: $link-text-color;
  }

  &.modal-open {
    pointer-events: none;
    user-select: none;

    &::after {
      content: '';
      z-index: 500;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(#000, 0.5);
    }
  }
}

/*
@media (max-width: 960px) {
  #kommunetesten-new {
    font-size: $font-size * 0.9;
  }
}
*/

@media (max-width: $responsive-break) {
  #kommunetesten-new {
    font-size: 14px;
  }
}
</style>

<style lang="scss" scoped>
@import "./assets/variables";

nav.breadcrumb-list {
  margin-top: 0.5em;
  margin-bottom: 4em;
}

#kommunetesten-new[data-embedded="true"] {
  nav.breadcrumb-list {
    margin-bottom: 2em;
  }
}

nav.counties {
  margin-top: 4em;
}

.button {
  margin-top: 2em;
  margin: 2em auto 0 auto;
  width: 80%;
}

footer {
  margin-top: 2em;
  text-align: center;
}

/*
@media (min-width: $responsive-break) {
  header {
    width: 80%;
    margin: 0 auto;
  }
}
*/
</style>

<script>
import Search from './components/Search.vue'
import Button from './components/Button.vue'
import Loading from './components/Loading.vue'
import Heading from './components/Heading.vue'
import ReadMore from './components/ReadMore.vue'
import CountyList from './components/CountyList.vue'
import Breadcrumbs from './components/Breadcrumbs.vue'
import Modal from './components/Modal.vue'
import PositionsText from './components/PositionsText.vue'
import { EventBus } from './bus.js'
import { mapGetters } from 'vuex'

export default {
  components: {
    Search,
    Button,
    Loading,
    Heading,
    ReadMore,
    CountyList,
    Breadcrumbs,
    Modal,
    PositionsText
  },

  computed: {
    county () {
      const id = this.$route.params.fid
        ? parseInt(this.$route.params.fid, 10)
        : null

      return id
        ? this.$store.getters.countyById(id)
        : this.resolvedCounty
    },

    resolvedCounty () {
      return this.municipality
        ? this.$store.getters.countyById(this.municipality.county)
        : undefined
    },

    municipality () {
      const id = this.$route.params.kid
        ? parseInt(this.$route.params.kid, 10)
        : undefined

      return id ? this.$store.getters.municipalityById(id, this.year) : null
    },

    year () {
      return this.$route.query.year
        ? parseInt(this.$route.query.year, 10)
        : 2019 // FIXME
    },

    embedded () {
      return document.querySelector('#kommunetesten-new')
        .getAttribute('data-iframe') === 'true'
    },

    ...mapGetters([
      'allMovements',
      'allYears',
      'filteredCounties'
    ])
  },

  methods: {
    onModalClose () {
      EventBus.$emit('modal-close')

      this.modal = false
    },

    onSearchSelect (item) {
      const link = item.kid
        ? this.municipalityLink(item.kid, { year: this.year })
        : this.countyLink(item.fid, { year: this.year })

      if (this.embedded && item.kid) {
        EventBus.$emit('modal-open', item)
      } else {
        this.$router.push(link)
      }
    },

    iframeExternalLink (row) {
      const { hostname, origin } = window.location
      const suffix = `#/municipality/${row.kid}?year=${this.year}`

      return hostname === 'localhost'
        ? `${origin}/${suffix}`
        : `https://www.forbrukerradet.no/kommunetesten/${suffix}`
    },

    calculateMovement (county, municipality, year) {
      /* eslint-disable camelcase */
      if (isNaN(county) && isNaN(municipality)) {
        return
      }

      const type = isNaN(county) ? 2 : 1
      const id = isNaN(county) ? municipality : county
      const linkMethod = type === 1 ? 'countyLink' : 'municipalityLink'

      // FIXME: For now pick the first movement in the table. This should probably be expanded
      // to take date into account at a later point !?
      const [movement] = this.allMovements.filter(iter => {
        return iter.type === type && iter.id === id
      })

      if (!movement) {
        return false
      }

      const moveIndex = this.allMovements.findIndex(m => m === movement)

      // This will redirect to the page where you select county because it was moved
      // Example: #/county/50?year=2019 -> 2016 -> Choices
      if (movement.type === 1 && movement.merged_legacy_ids.length > 0 && movement.changed_year >= year) {
        return this.countySelectionLink(id, {
          redirected: true,
          movement: moveIndex,
          year
        })
      }

      // Pick the "new" id
      // Example: #/municipality/1634?year=2019 -> #/municipality/5021?year=2019
      const newId = movement.changed_year < year
        ? movement.new_id || movement.merged_to_id
        : movement.legacy_id || movement.merged_legacy_ids

      if (!newId || (newId instanceof Array && !newId.length)) {
        return false
      }

      // Selects the next year from our available list of years, or use the current
      const newYear = this.allYears.filter(iter => {
        return (movement.changed_year > year)
          ? iter <= year
          : iter >= year
      })[0] || year

      // This will redirect to the page where you select county because it was moved
      // Example: /#/municipality/5054?year=2016 -> Choices
      if (newId instanceof Array) {
        return this.countySelectionLink(id, {
          redirected: true,
          movement: moveIndex,
          year: newYear
        })
      }

      // Then return appropriate route for our changes
      return this[linkMethod](newId, {
        year: newYear,
        redirected: true
      })
    },

    checkMovements () {
      if (this.$route.query.redirected) {
        return
      }

      const redirect = this.calculateMovement(
        parseInt(this.$route.params.fid, 10),
        parseInt(this.$route.params.kid, 10),
        this.year
      )

      if (redirect) {
        this.$router.push(redirect)
      }
    }
  },

  watch: {
    county () {
      this.checkMovements()
    }
  },

  updated () {
    this.checkMovements()
  },

  created () {
    // This event makes sure our openable components will close when clicked outside
    document.body.addEventListener('click', ev => {
      EventBus.$emit('body-clicked', ev)
    })

    document.body.addEventListener('touchend', ev => {
      EventBus.$emit('body-clicked', ev)
    })

    EventBus.$on('modal-open', row => {
      const municipalityCount = this.$store.getters
        .municipalityCount(this.year)

      const municipalities = this.$store.getters
        .municipalitiesByQuery(row.fid, this.year)

      const append = municipalities
        .find(iter => iter.kid === row.kid)

      this.modal = {
        municipalityCount,
        countyCount: municipalities.length,
        ...append
      }

      this.$el.classList.add('modal-open')
    })

    EventBus.$on('modal-close', () => {
      this.$el.classList.remove('modal-open')
    })

    return this.$store.dispatch('loadData', this.embedded)
      .then(() => (this.loading = false))
      .then(() => this.checkMovements())
  },

  data () {
    return {
      loading: true,
      modal: false
    }
  }
}
</script>
